// The client side of /api/directories (BUILD-BRIEF-R3 §6).
//
// `importCsv` is the door for a source that refuses to be read: SpherIQ's terms forbid
// automated reading, Zefix's robots.txt allows only two named search bots, and GREA and ARTISET
// publish no member list at all. The operator pastes an export and it becomes the SAME
// `organisation` proposals, with `evidence_kind 'document'`. A refusal is an outcome with a way
// round it, never a dead end.
//
// Note what `sync` can answer with: `refused` and a `note`. The Population zone renders that as
// a refusal in words — not as an error, and never as a count of zero.
import { qs, request, upload } from './client.js';
import type { Directory, ListResponse } from '../../shared/types.js';

export function listDirectories(params: { q?: string } = {}): Promise<ListResponse<Directory>> {
  return request(`/directories${qs(params)}`);
}

export function createDirectory(body: {
  name?: string;
  url?: string | null;
  federation?: string | null;
  cadence_hours?: number;
}): Promise<Directory & { note?: string }> {
  return request('/directories', { method: 'POST', body: JSON.stringify(body) });
}

/** What a sync answers. `members_seen` is null when nothing has counted yet — never 0. */
export interface DirectorySyncAnswer {
  members_seen: number | null;
  proposed: number;
  outcome?: 'unchanged' | 'changed' | 'signal' | 'error' | 'refused';
  /** 'terms' | 'robots' | 'no_list' | … — the source said no, and this says which no. */
  refused?: string;
  note?: string;
  error?: string;
  detail?: Record<string, unknown>;
}

/** Read this directory's list now. */
export function syncDirectory(id: number): Promise<DirectorySyncAnswer> {
  return request(`/directories/${id}/sync`, { method: 'POST' });
}

/** The operator's own CSV export of a member list, parsed deterministically. */
export function importDirectoryCsv(id: number, file: File): Promise<DirectorySyncAnswer> {
  const form = new FormData();
  form.append('file', file);
  return upload(`/directories/${id}/import`, form);
}

export interface DirectoryMembers {
  directory: Directory;
  /** NULL until a run counted. */
  members_seen: number | null;
  /** Organisations the record already holds with this federation on their raster. */
  on_record: Array<{ id: number; ref: string; name: string }>;
  /** Names from the list nobody has held yet: the population still waiting in Review. */
  pending_organisations: Array<{ id: number; ref: string; name: string; created_at: string }>;
  total: number;
}

export function directoryMembers(id: number): Promise<DirectoryMembers> {
  return request(`/directories/${id}/members`);
}
